import React, { useEffect, useState } from "react"
import logo from "./logo.svg"
import "./AdminApp.css"
import UserList from "components/UserList"
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  useQuery,
  gql,
  createHttpLink,
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import "firebaseConfig"
import { getAuth, onAuthStateChanged, signOut, getIdToken } from "firebase/auth"
import LoadingBlanket from "components/AdminLoadingBlanket"
import { Login } from "components/AdminLogin"
import { css } from "@emotion/css"

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null)
  const [simpleView, setSimpleView] = useState(false)
  useEffect(() => {
    onAuthStateChanged(getAuth(), (u) => {
      if (u) {
        setIsAuthenticated(true)
      } else {
        setIsAuthenticated(false)
      }
    })
  }, [])
  if (isAuthenticated === null) {
    return <LoadingBlanket />
  }
  if (isAuthenticated === false) {
    return <Login />
  }
  return (
    <ApolloProvider client={client}>
      {simpleView ? (
        <SimpleStaza onClickDetails={() => setSimpleView(false)} />
      ) : null}
      <UserList />
      <div
        onClick={() => signOut(getAuth())}
        className={css`
          opacity: 0.2;
          position: fixed;
          right: 0;
          bottom: 0;
          margin: 20px;
          background-color: #f7f7f7;
          cursor: pointer;
          padding: 10px 20px 10px 20px;
          border-radius: 10px;
          &:hover {
            background-color: #e7e7e7;
          }
        `}
      >
        <div>log out</div>
      </div>
    </ApolloProvider>
  )
}

const SimpleStaza = ({ onClickDetails }) => {
  const { data, loading, error } = useQuery(METRICS_QUERY)
  const metrics = data?._metrics as Metrics | null
  const numSignupsLastDay = metrics?.numSignupsLastDay
  if (!metrics) {
    return <LoadingBlanket />
  }
  return (
    <div
      style={{
        display: "flex",
        position: "fixed",
        width: "100%",
        height: "100%",
        backgroundColor: "white",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 100000,
      }}
    >
      <p
        style={{ fontSize: "10vh", cursor: "pointer" }}
        onClick={onClickDetails}
      >
        {numSignupsLastDay}
      </p>
    </div>
  )
}

const endpoint = process.env.REACT_APP_API_ENDPOINT ?? "localhost"
const port = process.env.REACT_APP_API_HTTP_PORT ?? "4000"
const useSSL = process.env.REACT_APP_USE_SSL === "true"

const uri = `http${useSSL ? "s" : ""}://${endpoint}${
  port ? `:${port}` : ""
}/graphql`

const authLink = setContext(async (_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = await getAuth().currentUser?.getIdToken()
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  }
})

const httpLink = createHttpLink({
  uri,
})

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
})

export type Metrics = {
  numUsers: number
  numPaidUsers: number
  numAnonUsers: number
  numInvitesThisWeek: number
  numDailyActive: number
  numWeeklyActive: number
  numMonthlyActive: number
  numSignupsLastDay: number
}

// staza – user list
export const METRICS_QUERY = gql`
  query METRICS {
    _metrics {
      numUsers
      numPaidUsers
      numAnonUsers
      numInvitesThisWeek
      numDailyActive
      numWeeklyActive
      numMonthlyActive
      numSignupsLastDay
    }
  }
`

export const METRICS_HISTORY_QUERY = gql`
  query METRICS_HISTORY {
    _metricsHistory {
      numUsers
      numPaidUsers
      numAnonUsers
      numInvitesThisWeek
      numDailyActive
      numWeeklyActive
      numMonthlyActive
      numSignupsLastDay
      date
    }
  }
`

export type User = {
  id: string
  username: string
  invitedBy: {
    id: string
    username: string
  }
  webNotificationPermission?: string
  numMobilePushTokens: number
  versionWeb?: string
  versionMobile?: string
  lastActiveAt?: string
  createdAt: string
  type: "anon" | "free" | "og" | "pro"
  numInvites: number
  numDaysActive: number
  numFilesCreated: number
  subscriptionProvider: "apple" | "stripe"
  signupPlatform: "mobile" | "web"
  onboardUserType: string
  banned?: boolean
  deleted?: boolean
  firstCommentedAt?: string
  firstCreatedGroupAt?: string
  firstCreatedFileAt?: string
  firstSavedMessageAt?: string
  totalBytesUploaded: number
  numContacts: number
  numPaywallHits: number
  numInviteClicks: number
  upgradedAt?: string
  downgradedAt?: string
  hasBeenContacted?: boolean
  fromInviteCode?: string
}

const userFields = `
  id
  username
  numInvites
  invitedBy {
    id
    username
  }
  webNotificationPermission
  numMobilePushTokens
  versionWeb
  versionMobile
  lastActiveAt
  createdAt
  type
  signupPlatform
  numDaysActive
  numFilesCreated
  subscriptionProvider
  onboardUserType
  deleted
  banned
  firstCommentedAt
  firstCreatedGroupAt
  firstCreatedFileAt
  firstSavedMessageAt
  totalBytesUploaded
  numContacts
  numPaywallHits
  numInviteClicks
  upgradedAt
  downgradedAt
  hasBeenContacted
  fromInviteCode
`

export const USERS_QUERY = gql`
  query USERS($sortBy: String, $invitedById: ID, $limit: Int, $offset: Int, $includeAnon: Boolean) {
    _users(
      sortBy: $sortBy
      invitedById: $invitedById
      limit: $limit
      offset: $offset
      includeAnon: $includeAnon
    ) {
      ${userFields}
    }
  }
`

export const USER_QUERY = gql`
  query USER($id: ID!) {
    _user(id: $id) {
      ${userFields}
    }
  }
`

export const UPDATE_USER = gql`
  mutation UPDATE_USER(
    $id: ID!
    $username: String
    $deleted: Boolean
    $banned: Boolean
  ) {
    _updateUser(
      id: $id
      username: $username
      deleted: $deleted
      banned: $banned
    )
  }
`

export default App
